/**
 * GeoFaqGenerator.js
 * Generates natural language FAQ (Q&A) content and E-E-A-T provenance context
 * for AI Answer Engines (SearchGPT, Perplexity, Gemini, Claude) and Schema.org FAQPage markup.
 */

export class GeoFaqGenerator {
  /**
   * Generate Q&A array for a tourist center in the requested language
   * @param {Object} center TouristCenter data object
   * @param {string} lang 'ko' | 'en' | 'ja' | 'zh'
   * @returns {Array<{question: string, answer: string}>}
   */
  static generateFaq(center, lang = 'ko') {
    const name = center.translatedName?.[lang] || center.name;
    const address = center.translatedAddress?.[lang] || center.address;
    const closed = center.translatedClosedDays?.[lang] || center.closedDays || (lang === 'ko' ? '연중무휴' : 'Open All Year');
    const summer = center.summerHours || '09:00~18:00';
    const winter = center.winterHours || summer;
    const operator = center.translatedOperator?.[lang] || center.operator || '관할 지자체';

    // 1. Language availability Q&A
    const availLangs = [];
    if (center.languages?.en) availLangs.push(lang === 'ko' ? '영어' : lang === 'ja' ? '英語' : lang === 'zh' ? '英语' : 'English');
    if (center.languages?.ja) availLangs.push(lang === 'ko' ? '일본어' : lang === 'ja' ? '日本語' : lang === 'zh' ? '日语' : 'Japanese');
    if (center.languages?.zh) availLangs.push(lang === 'ko' ? '중국어' : lang === 'ja' ? '中国語' : lang === 'zh' ? '中文' : 'Chinese');

    let q1, a1, q2, a2, q3, a3, q4, a4;

    if (lang === 'en') {
      q1 = `Is foreign language travel consultation available at ${name}?`;
      a1 = availLangs.length > 0
        ? `Yes, ${name} provides multilingual travel support in ${availLangs.join(', ')} along with official maps and travel brochures.`
        : `Basic travel information and printed guidebooks in multiple languages are provided on-site at ${name}.`;

      q2 = `What are the operating hours and regular closed days for ${name}?`;
      a2 = `Operating hours are ${summer} (Summer) and ${winter} (Winter). Regular closed days: ${closed}.`;

      q3 = `What convenient amenities and services are supported?`;
      const amenities = [];
      if (center.facilities?.luggage) amenities.push('Luggage Storage');
      if (center.facilities?.wheelchair) amenities.push('Wheelchair / Stroller Rental');
      if (center.facilities?.wifi) amenities.push('Free Wi-Fi');
      if (center.facilities?.stamp) amenities.push('Stamp Tour');
      a3 = amenities.length > 0
        ? `${name} supports convenient facilities including ${amenities.join(', ')}.`
        : `${name} provides free regional tourist maps, attraction leaflets, and local travel information.`;

      q4 = `Where is ${name} located?`;
      a4 = `It is located at ${address}. Operated and managed by ${operator}.`;
    } else if (lang === 'ja') {
      q1 = `${name}で外国語による旅行案内や相談は可能ですか？`;
      a1 = availLangs.length > 0
        ? `はい、${name}では${availLangs.join('、')}での観光案内および公式外国語ガイドマップ・パンフレットの配布を行っています。`
        : `現地では多言語観光パンフレットや観光マップをご利用いただけます。`;

      q2 = `営業時間と休業日はいつですか？`;
      a2 = `営業時間は夏季 ${summer}、冬季 ${winter}です。休業日案内：${closed}。`;

      q3 = `どのような旅行者向け便利サービスが利用できますか？`;
      const amenities = [];
      if (center.facilities?.luggage) amenities.push('手荷物預かり');
      if (center.facilities?.wheelchair) amenities.push('車椅子・ベビーカー対応');
      if (center.facilities?.wifi) amenities.push('無料Wi-Fi');
      if (center.facilities?.stamp) amenities.push('スタンプツアー');
      a3 = amenities.length > 0
        ? `${name}では${amenities.join('、')}などの便利サービスを提供しています。`
        : `無料の地域観光地図や観光名所パンフレット、交通案内を提供しています。`;

      q4 = `${name}の所在地はどこですか？`;
      a4 = `所在地は「${address}」です。運営・管理機関：${operator}。`;
    } else if (lang === 'zh') {
      q1 = `${name}提供外语旅游咨询服务吗？`;
      a1 = availLangs.length > 0
        ? `是的，${name}提供${availLangs.join('、')}专业旅游咨询服务，并提供多语言官方地图和旅行手册。`
        : `现场提供多语言旅游指南地图和景点宣传册。`;

      q2 = `营业时间与公休日是什么时候？`;
      a2 = `营业时间为夏季 ${summer}，冬季 ${winter}。公休日说明：${closed}。`;

      q3 = `提供哪些便民设施与旅行服务？`;
      const amenities = [];
      if (center.facilities?.luggage) amenities.push('行李寄存');
      if (center.facilities?.wheelchair) amenities.push('轮椅/婴儿车支持');
      if (center.facilities?.wifi) amenities.push('免费Wi-Fi');
      if (center.facilities?.stamp) amenities.push('印章收集游');
      a3 = amenities.length > 0
        ? `${name}提供${amenities.join('、')}等便民服务。`
        : `提供免费的地区旅游地图、景点导览手册以及交通咨询。`;

      q4 = `${name}的详细地址在哪里？`;
      a4 = `详细地址为：${address}。由 ${operator} 官方运营与管理。`;
    } else {
      // ko (Korean)
      q1 = `${name}에서 외국어 여행 상담이 가능한가요?`;
      a1 = availLangs.length > 0
        ? `네, ${name}에서는 ${availLangs.join(', ')} 통역 안내 인력 및 다국어 관광 지도/안내 책자를 상시 구비하여 지원합니다.`
        : `현장에서 다국어 관광 지도 및 안내 팜플렛을 무료로 수령하실 수 있습니다.`;

      q2 = `운영 시간과 휴무일은 어떻게 되나요?`;
      a2 = `운영 시간은 하절기 ${summer}, 동절기 ${winter}입니다. 정기 휴무일 안내: ${closed}.`;

      q3 = `제공되는 주요 편의시설과 여행자 서비스는 무엇이 있나요?`;
      const amenities = [];
      if (center.facilities?.luggage) amenities.push('무료/유료 짐보관');
      if (center.facilities?.wheelchair) amenities.push('휠체어 및 유모차 대여');
      if (center.facilities?.wifi) amenities.push('무료 공공 Wi-Fi');
      if (center.facilities?.stamp) amenities.push('스탬프 투어');
      a3 = amenities.length > 0
        ? `${name}에서는 ${amenities.join(', ')} 등의 편의를 편리하게 이용하실 수 있습니다.`
        : `지역 공식 관광 지도 무료 배포 및 대중교통/주변 명소 안내 서비스를 제공합니다.`;

      q4 = `${name}의 정확한 위치와 관리 기관은 어디인가요?`;
      a4 = `소재지는 '${address}'이며, ${operator}에서 공식 관리 및 운영하고 있습니다.`;
    }

    return [
      { question: q1, answer: a1 },
      { question: q2, answer: a2 },
      { question: q3, answer: a3 },
      { question: q4, answer: a4 }
    ];
  }

  /**
   * Generates E-E-A-T provenance disclaimer string
   * @param {Object} center 
   * @param {string} lang 
   * @returns {string}
   */
  static generateProvenance(center, lang = 'ko') {
    const syncDate = center.lastUpdated || '2026-08-15';
    if (lang === 'en') {
      return `Verified by Ministry of the Interior and Safety & Korea Tourism Data Portal (Sync: ${syncDate})`;
    } else if (lang === 'ja') {
      return `韓国行政安全部・韓国観光公社 公共データ検証済み (同期日: ${syncDate})`;
    } else if (lang === 'zh') {
      return `韩国行政安全部及韩国旅游数据门户官方验证数据 (同步日期: ${syncDate})`;
    }
    return `대한민국 행정안전부 및 한국관광공사 공공데이터 검증 완료 (최신 동기화: ${syncDate})`;
  }
}
